/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@mui/material";
import IconButton from "../../../components/ui/IconButton";
import SearchField from "../../../components/ui/SearchField";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import api from "../../../services/api";
import ConfirmDialog from "../../../components/ConfirmDialog";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useIntl } from "../../../contexts/IntlContext";
import { RoleGuard } from "../../../components/ui/RoleGuard";
import { GLOBAL_ROLES } from "../../../constants/roles";
import { PageHeader } from "../../../components/PageHeader";
import { TableEmptyState } from "../../../components/table/TableEmptyState";
import { TableSkeletonRows } from "../../../components/table/TableSkeletonRows";
import { StandardTablePagination } from "../../../components/table/StandardTablePagination";
import FiltersBar from "../../../components/ui/FiltersBar";


interface Column {
  id: "name" | "email" | "role" | "active" | "createdAt";
  label: string;
  minWidth?: number;
  align?: "right";
  hideOnMobile?: boolean;
}



interface User {
  userId: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
  createdAt: string;
}

export default function ListSystemUser() {
  const navigate = useNavigate();
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const columns: readonly Column[] = [
    { id: "name", label: intl.formatMessage({ id: "common.name" }), minWidth: 150 },
    { id: "email", label: intl.formatMessage({ id: "common.email" }), minWidth: 150 },
    { id: "role", label: intl.formatMessage({ id: "systemUser.role" }), minWidth: 100 },
    { id: "active", label: intl.formatMessage({ id: "common.active" }), minWidth: 100 },
    { id: "createdAt", label: intl.formatMessage({ id: "systemUser.createdAt" }), minWidth: 150, hideOnMobile: true },
  ];

  const visibleColumns = columns.filter((col) => !isMobile || !col.hideOnMobile);

  const [users, setUsers] = useState<User[]>([]);
  const [count, setCount] = useState(0);
  const [selectedUser, setSelectedUser] = useState<User>();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);


  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleClick = (_event: React.MouseEvent<unknown>, id: string) => {
    const user = users.find((u) => u.userId === id);
    if (!user) return;
    setSelectedUser((prev) => (prev?.userId === id ? undefined : user));
  };

  async function fetchUsers() {
    setListLoading(true);
    try {
      const response = await api.get("/app/users", {
        params: {
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          search: search.trim().length > 0 ? search : undefined,
        },
      });

      setUsers(response.data.data);
      setCount(response.data.count ?? 0);
    } catch (err) {
      toast.error(intl.formatMessage({ id: "systemUser.fetchError" }));
    } finally {
      setListLoading(false);
    }
  }

  useEffect(() => {
    if (search.trim().length > 0 && search.trim().length < 3) return;

    const timer = setTimeout(() => {
      fetchUsers();
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [search]);

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage]);

  async function handleDeleteUser() {
    try {
      await api.delete(`/app/users/${selectedUser!.userId}`);

      const newUsers = users.filter(
        (user) => user.userId !== selectedUser!.userId
      );

      setUsers(newUsers);
      setSelectedUser(undefined);
      setModalOpen(false);
      toast.success(intl.formatMessage({ id: "systemUser.deleteSuccess" }));
    } catch (error) {
      // @ts-ignore
      if (error?.response?.status === 400 || error?.response?.status === 401) {
        // @ts-ignore
        toast.error(error.response.data.message);
      } else {
        toast.error(intl.formatMessage({ id: "login.connectionError" }));
      }
    }
  }

  async function handleSendPasswordReset() {
    setResetConfirmOpen(false);
    setSendingReset(true);
    try {
      await api.post('app/auth/forgot-password', {
        email: selectedUser!.email,
      });
      toast.success(
        intl.formatMessage(
          { id: "systemUser.passwordResetSuccess" },
          { email: selectedUser!.email }
        )
      );
    } catch (error) {
      toast.error(intl.formatMessage({ id: "systemUser.passwordResetError" }));
    } finally {
      setSendingReset(false);
    }
  }

  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <PageHeader
        title={intl.formatMessage({ id: "systemUser.title" })}
        isSelectionActive={!!selectedUser}
        loading={listLoading}
        sx={{ px: 2, py: 1 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <IconButton
            tooltip={intl.formatMessage({ id: "systemUser.add" })}
            onClick={() => navigate("/usuario/new")}
          >
            <AddIcon />
          </IconButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

          <span>
            <IconButton
              tooltip={selectedUser ? intl.formatMessage({ id: "systemUser.edit" }) : intl.formatMessage({ id: "systemUser.selectHint" })}
              disabled={!selectedUser}
              onClick={() =>
                selectedUser && navigate(`/usuario/${selectedUser.userId}`)
              }
            >
              <EditIcon />
            </IconButton>
          </span>

          <RoleGuard allowedRoles={GLOBAL_ROLES}>
            <span>
              <IconButton
                tooltip={selectedUser ? intl.formatMessage({ id: "systemUser.sendPasswordReset" }) : intl.formatMessage({ id: "systemUser.selectHint" })}
                onClick={() => setResetConfirmOpen(true)}
                disabled={!selectedUser || sendingReset}
              >
                {sendingReset ? <CircularProgress size={20} /> : <LockResetIcon />}
              </IconButton>
            </span>

            <span>
              <IconButton
                tooltip={selectedUser ? intl.formatMessage({ id: "systemUser.delete" }) : intl.formatMessage({ id: "systemUser.selectHint" })}
                onClick={() => setModalOpen(true)}
                disabled={!selectedUser}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </RoleGuard>
        </Box>
      </PageHeader>

      <Box sx={{ px: 2, pb: 2 }}>
        <FiltersBar activeFilterCount={search ? 1 : 0} sticky={false}>
          <SearchField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={intl.formatMessage({ id: "systemUser.searchPlaceholder" })}
            sx={{ width: { xs: "100%", sm: 220 }, marginY: 0 }}
          />
        </FiltersBar>
      </Box>

      <TableContainer sx={{ maxHeight: '65vh' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {listLoading ? (
              <TableSkeletonRows
                rows={rowsPerPage > 10 ? 10 : rowsPerPage}
                columns={visibleColumns.length}
              />
            ) : users.length === 0 ? (
              <TableEmptyState colSpan={visibleColumns.length}>
                {intl.formatMessage({ id: "systemUser.empty" })}
              </TableEmptyState>
            ) : (
              users.map((row) => (
                <TableRow
                  hover
                  key={row.userId}
                  role="row"
                  selected={selectedUser?.userId === row.userId}
                  onClick={(e) => handleClick(e, row.userId)}
                  sx={{ cursor: "pointer" }}
                >
                  {visibleColumns.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.id === "createdAt"
                          ? new Date(value as string).toLocaleDateString("pt-BR")
                          : column.id === "active"
                            ? value
                              ? intl.formatMessage({ id: "systemUser.yes" })
                              : intl.formatMessage({ id: "systemUser.no" })
                            : column.id === "role"
                              ? intl.formatMessage({ id: `appBar.role.${value}` } as any)
                              : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <StandardTablePagination
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <ConfirmDialog
        open={modalOpen}
        title={intl.formatMessage({ id: "common.confirm" })}
        message={intl.formatMessage({ id: "deleteDialog.confirmMessage" })}
        confirmLabel={intl.formatMessage({ id: "common.yes" })}
        cancelLabel={intl.formatMessage({ id: "common.no" })}
        onConfirm={handleDeleteUser}
        onClose={() => setModalOpen(false)}
      />

      <ConfirmDialog
        open={resetConfirmOpen}
        title={intl.formatMessage({ id: "systemUser.sendPasswordReset" })}
        message={selectedUser ? intl.formatMessage(
          { id: "systemUser.passwordResetConfirmMessage" },
          { email: selectedUser.email }
        ) : ""}
        confirmLabel={intl.formatMessage({ id: "common.confirm" })}
        cancelLabel={intl.formatMessage({ id: "common.cancel" })}
        onConfirm={handleSendPasswordReset}
        onClose={() => setResetConfirmOpen(false)}
      />

    </Paper>
    </>
  );
}
